import {FC, ReactElement, useEffect, useState} from "react";
import {TreeItem2, SimpleTreeView, treeItemClasses} from "@mui/x-tree-view";
import {ClientEntity} from "@common-core/coat-operational-hierarchy-appsync-model";
import {Spinner} from "@interstate/components/Spinner";
import {useInterstateToken} from "@interstate/components/InterstateThemeProvider";
import {ChevronDownIcon, ChevronRightIcon} from "@interstate/components/Icons";
import {TabColumn} from "../../../tab-content";
import {useClientEntity} from "../../context";
import {collectClientSelectionPath} from "./collectClientSelectionPath";
import {ClientHierarchyNodeLabel} from "./ClientHierarchyNodeLabel";
import "./ClientHierarchyHierarchyColumn.scss";

export const ClientHierarchyHierarchyColumn: FC = () => {
  const token = useInterstateToken();
  const [expanded, setExpanded] = useState<string[]>(() => []);
  const [root, setRoot] = useState<ClientEntity>();
  const {
    entity: {caId, hierarchy}
  } = useClientEntity();

  const renderTree = (node: ClientEntity): ReactElement => {
    const selected = node.caId === caId;
    const children = node.children ? [...node.children] : [];
    return (
      <TreeItem2
        key={node.caId}
        itemId={node.caId}
        data-testid={`client-hierarchy-node-${node.caId}`}
        className={`client-hierarchy-node${selected ? " client-hierarchy-node--selected" : ""}`}
        label={<ClientHierarchyNodeLabel node={node} />}>
        {children
          ?.toSorted((a, b) => {
            if (expanded.includes(a.caId)) {
              return -1;
            }
            if (expanded.includes(b.caId)) {
              return 1;
            }
            return a.custName.localeCompare(b.custName);
          })
          .map(child => renderTree(child))}
      </TreeItem2>
    );
  };

  useEffect(() => {
    if (hierarchy) {
      const expanded: string[] = [];
      if (collectClientSelectionPath(hierarchy, caId, expanded)) {
        setExpanded(expanded);
        setRoot(hierarchy);
      }
    }
  }, [hierarchy, caId]);

  if (!hierarchy || root === undefined || expanded.length === 0) {
    return (
      <TabColumn name={"client-hierarchy-column"}>
        <Spinner />
      </TabColumn>
    );
  }

  return (
    <TabColumn name={"client-hierarchy-column"}>
      <SimpleTreeView
        id={`client-hierarchy-tree-view-${root?.caId}`}
        data-testid={`client-hierarchy-tree-view-${root?.caId}`}
        className={"client-hierarchy-tree-view"}
        sx={{
          [`& .${treeItemClasses.groupTransition}`]: {
            marginLeft: 1.9,
            borderLeft: theme => `1px solid ${theme.palette.divider}`
          }
        }}
        slots={{
          expandIcon: ChevronRightIcon,
          collapseIcon: ChevronDownIcon
        }}
        slotProps={{
          collapseIcon: {
            color: token("sem.color.on-surface.primary")
          },
          expandIcon: {
            color: token("sem.color.on-surface.primary")
          }
        }}
        disableSelection={true}
        disabledItemsFocusable={true}
        defaultExpandedItems={expanded}
        selectedItems={caId}>
        {renderTree(root)}
      </SimpleTreeView>
    </TabColumn>
  );
};
